import axios from 'axios'
import store from '@/store'
import { Toast, Dialog } from 'vant'
import router from '@/router'
import { removeToken } from '@/utils/auth'
import {
    isCacheableRequest,
    getCacheKey,
    getCacheTTL,
    readApiCache,
    writeApiCache,
    clearApiCache,
    revalidateApiCache,
} from '@/utils/apiCache'

const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 10000
})

service.interceptors.request.use(
    config => {
        if (store.state.user.token) {
            config.headers['Authorization'] = `Bearer ${store.state.user.token}`
        }

        if (isCacheableRequest(config)) {
            const cacheKey = getCacheKey(config, store.state.user.token)
            const cachedData = readApiCache(cacheKey)
            config.apiCacheKey = cacheKey
            config.apiCacheTTL = getCacheTTL(config)

            // null 以外都视为有效命中（含 false / 0 / '' / []）
            if (cachedData !== null) {
                config.adapter = () => Promise.resolve({
                    data: cachedData,
                    status: 200,
                    statusText: 'OK',
                    headers: {},
                    config,
                    request: null
                })
                config.fromApiCache = true

                // 后台刷新，避免公告/配置等长时间吃旧数据
                revalidateApiCache(
                    cacheKey,
                    () => service.request({
                        method: config.method,
                        url: config.url,
                        params: config.params,
                        baseURL: config.baseURL,
                        headers: config.headers,
                        timeout: config.timeout,
                        skipApiCache: true,
                    }),
                    config.apiCacheTTL
                )
            }
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 标记是否正在重定向到登录页面，防止多次重定向
let isRedirecting = false
service.interceptors.response.use(
    response => {
        const method = (response.config?.method || 'get').toLowerCase()

        if (response.config?.apiCacheKey && !response.config?.fromApiCache) {
            writeApiCache(
                response.config.apiCacheKey,
                response.data,
                response.config.apiCacheTTL
            )
        } else if (method !== 'get') {
            clearApiCache()
        }

        return response.data
    },
    error => {
        // 网络失败时尝试返回过期缓存兜底
        if (error.config?.apiCacheKey && !error.response) {
            const staleData = readApiCache(error.config.apiCacheKey, true)
            if (staleData !== null) {
                return Promise.resolve(staleData)
            }
        }

        // 确保错误对象和错误响应存在
        if (error.response) {
            const status = error.response.status;

            // 解决在default中使用let的ESLint错误
            let errorMsg = '请求失败，请稍后再试';
            try {
                if (error.response.data && typeof error.response.data.msg === 'string') {
                    errorMsg = error.response.data.msg;
                }
            } catch (e) {
                console.error('处理错误消息时出错', e);
            }

            switch (status) {
                case 401:
                    // 防止多个请求同时触发多次跳转
                    if (!isRedirecting) {
                        isRedirecting = true

                        // 显示提示信息
                        Toast.fail('登录已过期，请重新登录')

                        clearApiCache()

                        // 清除用户状态
                        store.dispatch('user/logout').then(() => {
                            // 移除token
                            removeToken()

                            // 记录当前路径，以便登录后可以返回
                            const currentPath = router.currentRoute.value.fullPath

                            // 跳转到登录页面，并带上原目标路径
                            router.push({
                                path: '/login',
                                query: currentPath !== '/login' ? { redirect: currentPath } : {}
                            }).finally(() => {
                                // 重置重定向标志
                                setTimeout(() => {
                                    isRedirecting = false
                                }, 1000)
                            })
                        })
                    }
                    break;

                case 403:
                    // 权限不足
                    Toast.fail('您没有权限执行此操作')
                    router.push('/403')
                    break;

                case 500:
                    Toast.fail('服务器错误，请稍后再试')
                    break;

                default:
                    // 业务页自行处理错误提示时，跳过全局弹窗
                    if (error.config?.skipErrorDialog) {
                        break;
                    }
                    Dialog.alert({
                        message: errorMsg
                    }).catch(() => {});
            }
        } else {
            // 网络错误等情况
            Toast.fail('网络连接失败，请检查您的网络');
        }

        return Promise.reject(error)
    }
)

export default service
export { clearApiCache }
